/**
 * Excel's own styling, as CSS — so a workbook reads in the viewer the way it reads in Excel.
 *
 * The exports this product writes carry explicit colours, column widths, merges and number
 * formats (`excel-styles.constants.ts`), and dropping them turned a formatted cap table into a
 * grey grid of numbers. Everything here is a pure translation of one Excel value; what reads the
 * workbook keeps no knowledge of CSS, and these keep none of ExcelJS.
 */

/** Excel writes `AARRGGBB`; the web wants `#RRGGBB` and has its own notion of transparent. */
export const cssColour = (argb: null | string | undefined): string | undefined => {
  if (!argb) return undefined;

  const hex = argb.length === 8 ? argb.slice(2) : argb;

  return /^[0-9a-fA-F]{6}$/.test(hex) ? `#${hex}` : undefined;
};

/**
 * A column's width is in characters of the default font, not pixels — Excel's own conversion is
 * `width * 7 + 5` for an 11pt Calibri digit, which is the figure every spreadsheet tool uses.
 */
export const DEFAULT_COLUMN_WIDTH = 8.43;

export const columnWidthPx = (width: null | number | undefined): number =>
  Math.round((width ?? DEFAULT_COLUMN_WIDTH) * 7 + 5);

/** A row's height is in points; the web is in pixels, at 96 to the inch against Excel's 72. */
export const rowHeightPx = (height: null | number | undefined): number | undefined =>
  height ? Math.round((height * 96) / 72) : undefined;

const BORDER_WIDTH_PX: Record<string, number> = {
  dashed: 1,
  dotted: 1,
  double: 3,
  hair: 1,
  medium: 2,
  thick: 3,
  thin: 1,
};

export const borderWidthPx = (style: null | string | undefined): number =>
  style ? (BORDER_WIDTH_PX[style] ?? 1) : 0;

// Excel's vertical alignment names are not the web's, and its default is the bottom of the cell.
const VERTICAL_ALIGN: Record<string, string> = {
  bottom: 'bottom',
  justify: 'middle',
  middle: 'middle',
  top: 'top',
};

export const verticalAlign = (alignment: null | string | undefined): string =>
  VERTICAL_ALIGN[alignment ?? ''] ?? 'bottom';

/**
 * Where a cell's text sits when the file does not say.
 *
 * Excel's General alignment is not "left": a number sits right and text sits left, which is what
 * makes a column of figures line up on the decimal point. A reader that left-aligns everything
 * loses that at a glance, so the default follows the value's type.
 */
export const horizontalAlign = (
  alignment: null | string | undefined,
  isNumeric: boolean,
): string => {
  if (alignment && alignment !== 'general') return alignment === 'justify' ? 'justify' : alignment;

  return isNumeric ? 'right' : 'left';
};
